import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Calendar } from 'lucide-react';
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface DateRangePickerProps {
  value: {
    start: string;
    end: string;
  };
  onChange: (value: { start: string; end: string }) => void;
}

const presets = [
  {
    label: 'Today',
    getValue: () => ({
      start: format(new Date(), 'yyyy-MM-dd'),
      end: format(new Date(), 'yyyy-MM-dd')
    })
  },
  {
    label: 'Yesterday',
    getValue: () => ({
      start: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
      end: format(subDays(new Date(), 1), 'yyyy-MM-dd')
    })
  },
  {
    label: 'Last 7 days',
    getValue: () => ({
      start: format(subDays(new Date(), 6), 'yyyy-MM-dd'),
      end: format(new Date(), 'yyyy-MM-dd')
    })
  },
  {
    label: 'Last 30 days',
    getValue: () => ({
      start: format(subDays(new Date(), 29), 'yyyy-MM-dd'),
      end: format(new Date(), 'yyyy-MM-dd')
    })
  },
  {
    label: 'This week',
    getValue: () => ({
      start: format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'),
      end: format(endOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd')
    })
  },
  {
    label: 'This month',
    getValue: () => ({
      start: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
      end: format(endOfMonth(new Date()), 'yyyy-MM-dd')
    })
  }
];

export function DateRangePicker({ value, onChange }: DateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);

  const formatDisplayDate = (date: string) => {
    return date ? format(new Date(date), 'MMM d, yyyy') : '';
  };

  const handlePresetChange = (preset: string) => {
    const selectedPreset = presets.find(p => p.label === preset);
    if (selectedPreset) {
      const range = selectedPreset.getValue();
      onChange(range);
      setIsOpen(false);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal",
            !value.start && "text-muted-foreground"
          )}
        >
          <Calendar className="mr-2 h-4 w-4" />
          {value.start ? (
            <>
              {formatDisplayDate(value.start)} - {formatDisplayDate(value.end)}
            </>
          ) : (
            <span>Pick a date range</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex flex-col space-y-4 p-4">
          <Select
            onValueChange={handlePresetChange}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a preset" />
            </SelectTrigger>
            <SelectContent>
              {presets.map((preset) => (
                <SelectItem key={preset.label} value={preset.label}>
                  {preset.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex gap-4">
            <div>
              <div className="text-sm font-medium mb-2">Start date</div>
              <CalendarComponent
                mode="single"
                selected={value.start ? new Date(value.start) : undefined}
                onSelect={(date) => {
                  onChange({ ...value, start: date ? format(date, 'yyyy-MM-dd') : '' });
                }}
                initialFocus
              />
            </div>
            <div>
              <div className="text-sm font-medium mb-2">End date</div>
              <CalendarComponent
                mode="single"
                selected={value.end ? new Date(value.end) : undefined}
                onSelect={(date) => {
                  onChange({ ...value, end: date ? format(date, 'yyyy-MM-dd') : '' });
                }}
                disabled={(date) => {
                  if (!value.start) return false;
                  return date < new Date(value.start);
                }}
              />
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
